import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PlanOverview } from "@/pages/broker/PlanDetailComponents/PlanOverview";
import PlanRates from "@/pages/broker/PlanDetailComponents/PlanRates";
import { PlanDocumentsManager } from "@/components/plans/PlanDocuments";
import { TemplatedBenefitsEditor, normalizeRawBenefits, type RawBenefitLine } from "@/components/benefits/TemplatedBenefitsEditor";
import { WbBenefitsBlock } from "@/components/benefits/detail/blocks/WbBenefitsBlock";
import { getBenefitFieldTemplate } from "@shared/constants/benefitFieldTemplates";
import {
  ArrowLeft,
  Plus,
  FileText,
  AlertTriangle,
  Settings,
  Info,
  Upload,
} from "lucide-react";
import type { PlanWithCarrier } from "@shared/schema";
import { onePlanYearLinkPerPlan } from "@shared/utils/planYearLinks";
import { isLifeDisabilityType, isSupplementalType, isFSAType } from "@shared/schema";
import { showsBenefitsTab, benefitsTabDescription, tabGridCols } from "./PlanDetailComponents/benefitsTab";
import { isFsaConfigured } from "@/lib/fsaPreview";
import PlanContributions from "@/pages/broker/PlanDetailComponents/PlanContributions";
import EligibilityGroupsPanel from "@/pages/broker/PlanDetailComponents/EligibilityGroupsPanel";
import { MultiSelect } from "@/components/ui/multi-select";
import { US_STATES } from "@/lib/usStates";
import { useOrgOptions } from "@/pages/broker/PlanDetailComponents/useOrgOptions";
import { LifeDisabilityConfigForm } from "@/components/plans/LifeDisabilityConfigForm";
import { FSAConfigForm } from "@/components/plans/FSAConfigForm";
import { SupplementalConfigForm } from "@/components/plans/SupplementalConfigForm";
import { useDocuments } from "@/hooks/useDocuments";
import { DocumentList } from "@/components/ui/document-list";
import { DocumentUpload } from "@/components/ui/document-upload";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { PlanFormLibraryPanel } from "@/components/form-library/PlanFormLibraryPanel";

// Add a local type for benefit configuration
type BenefitConfig = {
  description: string;
  inNetworkDeductible?: number;
  outNetworkDeductible?: number;
  inNetworkCoinsurance?: number;
  outNetworkCoinsurance?: number;
  inNetworkCopay?: number;
  outNetworkCopay?: number;
  maxOutOfPocket?: number;
  annualMaximum?: number;
  deductibleApply?: boolean;
};

// Small group picker for the Contributions sub-tab. The full CRUD lives on
// the Eligibility tab; this just lets the broker switch which group's
// contributions they're editing without leaving the page.
function ActiveGroupPicker({
  clientId,
  planId,
  activeRuleSetId,
  onChange,
}: {
  clientId: number;
  planId: number;
  activeRuleSetId: number | null;
  onChange: (ruleSetId: number | null, groupId: number | null) => void;
}) {
  const { data: groups = [] } = useQuery<any[]>({
    queryKey: ["/api/eligibility-groups", clientId, planId],
    queryFn: async () => {
      const res = await fetch(
        `/api/eligibility-groups?clientId=${clientId}&planId=${planId}`,
      );
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Auto-select first group on first load
  useEffect(() => {
    if (activeRuleSetId == null && groups.length > 0) {
      onChange(groups[0].contributionRuleSetId, groups[0].id);
    }
  }, [activeRuleSetId, groups, onChange]);

  if (groups.length === 0) return null;

  const activeGroup =
    groups.find((g) => g.contributionRuleSetId === activeRuleSetId) || groups[0];

  return (
    <div className="rounded-md border bg-blue-50/40 dark:bg-blue-950/30 p-3 flex items-center justify-between gap-3">
      <div className="text-sm">
        Editing contributions for group:{" "}
        <strong>
          {activeGroup?.name || (activeGroup?.priority === 0 ? "Default group" : `Group ${activeGroup?.id}`)}
        </strong>
      </div>
      <Select
        value={String(activeGroup?.id ?? "")}
        onValueChange={(v) => {
          const g = groups.find((x) => String(x.id) === v);
          if (g) onChange(g.contributionRuleSetId, g.id);
        }}
      >
        <SelectTrigger className="w-64">
          <SelectValue placeholder="Pick a group…" />
        </SelectTrigger>
        <SelectContent>
          {groups.map((g) => (
            <SelectItem key={g.id} value={String(g.id)}>
              {g.name || (g.priority === 0 ? "Default group" : `Group ${g.id}`)} · priority {g.priority}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function LifeDisabilityContributions({ planId }: { planId: string; clientId?: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [fundingType, setFundingType] = useState<"employer" | "employee">("employer");
  const [percentage, setPercentage] = useState("100");
  const [loaded, setLoaded] = useState(false);

  const { data: planContributions = [] } = useQuery({
    queryKey: ["/api/plan-contributions"],
  });

  // Load existing contribution data
  useEffect(() => {
    if (loaded) return;
    const contributions = Array.isArray(planContributions)
      ? planContributions.find((pc: any) => pc.planId === parseInt(planId || "0"))
      : null;
    if (contributions?.rows?.length) {
      const row = contributions.rows[0];
      setFundingType(row.payer === "employee" ? "employee" : "employer");
      setPercentage(row.percentage != null ? String(row.percentage) : "100");
      setLoaded(true);
    } else if (Array.isArray(planContributions) && planContributions.length > 0) {
      // Data loaded but no rows for this plan — keep defaults
      setLoaded(true);
    }
  }, [planContributions, planId, loaded]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("PUT", `/api/plan-contributions/${planId}`, {
        rows: [{
          contributionType: "tier_rates",
          contributionTypeValue: "employee_only",
          payer: fundingType,
          percentage: parseFloat(percentage) || 100,
          nominalAmount: null,
          maxAmount: null,
        }],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/plan-contributions"] });
      toast({ title: "Success", description: "Contribution settings saved successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save contributions", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contribution Configuration</CardTitle>
        <CardDescription>
          Configure how this plan is funded — employer-sponsored or voluntary.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="space-y-2">
            <Label>Funding Type</Label>
            <Select value={fundingType} onValueChange={(v: "employer" | "employee") => setFundingType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employer">Employer-Sponsored</SelectItem>
                <SelectItem value="employee">Voluntary (Employee-Paid)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{fundingType === "employer" ? "Employer" : "Employee"} Contribution</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={100}
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} size="sm">
            {saveMutation.isPending ? "Saving..." : "Save Contributions"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlanDetail() {
  const [, params] = useRoute("/plans/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isEmployer = (user as any)?.role === "employer";
  const planId = params?.id;

  const PLAN_TABS = ["overview", "pricing", "benefits", "eligibility", "coverage-config", "fsa-config", "forms"] as const;
  const PRICING_TABS = ["rates", "contributions"] as const;
  const [activeTab, setActiveTab] = useQueryState("tab", parseAsStringLiteral(PLAN_TABS).withDefault("overview"));
  const [pricingInnerTab, setPricingInnerTab] = useQueryState("ptab", parseAsStringLiteral(PRICING_TABS).withDefault("rates"));
  // DEV-148: active eligibility group's rule set. Drives which group's
  // contributions the PlanContributions editor reads/writes.
  const [activeRuleSetId, setActiveRuleSetId] = useState<number | null>(null);
  const [selectedBenefit, setSelectedBenefit] = useState<string | null>(null);
  const [benefitDetails, setBenefitDetails] = useState<
    Record<string, BenefitConfig>
  >({});
  const [contingencyType, setContingencyType] = useState<string>("");
  // Templated plan types (health/dental/vision/life/...) edit plans.rawBenefits
  // with the same field set as the Plan Builder; the legacy category editor
  // below only remains for types without a template.
  const [rawBenefitsDraft, setRawBenefitsDraft] = useState<RawBenefitLine[]>([]);
  const [rawBenefitsDirty, setRawBenefitsDirty] = useState(false);
  const [contingencyPlanId, setContingencyPlanId] = useState<
    number | undefined
  >(undefined);

  // Autosave timeout ref
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced persist of benefit details to localStorage and backend
  const scheduleAutosave = (nextDetails: Record<string, BenefitConfig>) => {
    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(() => {
      if (!planId) return;
      const storageKey = `client_${
        clientId || "global"
      }_plan_${planId}_benefits`;
      const toPersist: Record<string, any> = {
        ...nextDetails,
        savedAt: new Date().toISOString(),
      };
      try {
        localStorage.setItem(storageKey, JSON.stringify(toPersist));
      } catch {}

      // Only send items that have a non-empty description
      const toSave: Record<string, BenefitConfig> = {};
      Object.entries(nextDetails).forEach(([k, v]) => {
        if (
          v &&
          typeof v.description === "string" &&
          v.description.trim() !== ""
        ) {
          toSave[k] = v;
        }
      });
      if (Object.keys(toSave).length > 0) {
        saveBenefitsMutation.mutate(toSave);
      }
    }, 700);
  };

  // Open advanced configuration modal for a category
  const openAdvanced = (category: string, label?: string) => {
    setAdvancedCategory(category);
    setAdvancedLabel(label || category);
    const cfg = benefitDetails[category] || { description: "" };
    setAdvancedDescription(cfg.description || "");
    setAdvancedFields({
      inNetworkDeductible: cfg.inNetworkDeductible?.toString() || "",
      outNetworkDeductible: cfg.outNetworkDeductible?.toString() || "",
      inNetworkCoinsurance: cfg.inNetworkCoinsurance?.toString() || "",
      outNetworkCoinsurance: cfg.outNetworkCoinsurance?.toString() || "",
      inNetworkCopay: cfg.inNetworkCopay?.toString() || "",
      outNetworkCopay: cfg.outNetworkCopay?.toString() || "",
      maxOutOfPocket: cfg.maxOutOfPocket?.toString() || "",
      annualMaximum: cfg.annualMaximum?.toString() || "",
      deductibleApply: cfg.deductibleApply ? "true" : "false",
    });
    setAdvancedOpen(true);
  };

  // Get context IDs from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get("clientId") || urlParams.get("client");
  const prospectId = urlParams.get("prospectId") || urlParams.get("prospect");

  // Check if clientId is a valid numeric ID (not a UUID/agency ID)
  const isValidNumericClientId = clientId && !isNaN(parseInt(clientId, 10)) && parseInt(clientId, 10) > 0;
  const clientIdNumber = clientId ? Number(clientId) : undefined;
  // planId comes from the route as a string. The eligibility-group query key
  // and its invalidations MUST use the same type, or invalidateQueries (which
  // compares each key element by equality) silently won't match and the
  // inline Eligibility Requirements form never refetches after a save.
  const planIdNumber = planId ? Number(planId) : undefined;

  // DEV-148: eligibility groups for this (client, plan). The Eligibility
  // Requirements card below edits whichever group is "active" — defaults to
  // the primary (lowest priority) group, but the broker can switch via the
  // group picker or by clicking a group in the EligibilityGroupsPanel.
  const { data: eligibilityGroupsAll = [] } = useQuery<any[]>({
    queryKey: ["/api/eligibility-groups", clientIdNumber, planIdNumber],
    enabled: !!clientIdNumber && !!planIdNumber,
    queryFn: async () => {
      const res = await fetch(
        `/api/eligibility-groups?clientId=${clientIdNumber}&planId=${planIdNumber}`,
      );
      if (!res.ok) return [];
      return res.json();
    },
  });
  const [activeEligibilityGroupId, setActiveEligibilityGroupId] = useState<
    number | null
  >(null);
  const activeEligibilityGroup =
    eligibilityGroupsAll.find((g) => g.id === activeEligibilityGroupId) ??
    eligibilityGroupsAll[0] ??
    null;

  // Auto-select primary on first load
  useEffect(() => {
    if (activeEligibilityGroupId == null && eligibilityGroupsAll.length > 0) {
      setActiveEligibilityGroupId(eligibilityGroupsAll[0].id);
    }
  }, [activeEligibilityGroupId, eligibilityGroupsAll]);

  const orgOptionsForEligibility = useOrgOptions(clientIdNumber);

  const markRatesReviewedMutation = useMutation({
    mutationFn: async () => {
      if (!planId) throw new Error("Missing plan id");
      await apiRequest("PATCH", `/api/plans/${planId}`, {
        needsRateReview: false,
      });
    },
    onSuccess: async () => {
      if (planId) {
        queryClient.setQueryData([`/api/plans/${planId}`], (prev: any) => {
          if (!prev || typeof prev !== "object") return prev;
          return { ...prev, needsRateReview: false };
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/plans"] }),
        planId
          ? queryClient.invalidateQueries({ queryKey: [`/api/plans/${planId}`] })
          : Promise.resolve(),
        clientIdNumber
          ? queryClient.invalidateQueries({
              queryKey: ["/api/client-plans", clientIdNumber],
            })
          : Promise.resolve(),
      ]);

      toast({
        title: "Marked reviewed",
        description: "The rate review flag has been cleared.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to mark reviewed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Advanced fields dialog state
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedCategory, setAdvancedCategory] = useState<string | null>(null);
  const [advancedLabel, setAdvancedLabel] = useState<string>("");
  const [advancedDescription, setAdvancedDescription] = useState("");
  const [advancedFields, setAdvancedFields] = useState({
    inNetworkDeductible: "",
    outNetworkDeductible: "",
    inNetworkCoinsurance: "",
    outNetworkCoinsurance: "",
    inNetworkCopay: "",
    outNetworkCopay: "",
    maxOutOfPocket: "",
    annualMaximum: "",
    deductibleApply: "false",
  });

  // Life/Disability rate config state for inline editing in Rates & Contributions tab
  const [rateConfigData, setRateConfigData] = useState<Record<string, any>>({});
  const [rateConfigLoading, setRateConfigLoading] = useState(false);
  const [rateConfigSaving, setRateConfigSaving] = useState(false);

  // Inline age-band state for life/disability tiered rates
  const [ldAgeBandType, setLdAgeBandType] = useState<string>("all_ages");
  const [ldAgeBandRates, setLdAgeBandRates] = useState<Record<string, string>>({});
  const [ldAgeBandTobaccoRates, setLdAgeBandTobaccoRates] = useState<Record<string, string>>({});
  const [ldAgeBandLoading, setLdAgeBandLoading] = useState(false);

  // Plan forms/documents hook (must be top-level, not inside render)
  const {
    documents: forms,
    loading: formsLoading,
    error: formsError,
    refreshDocuments: formsRefresh,
  } = useDocuments({ category: "form", status: "active" });

  // Plan form associations from the Form Library (broker + carrier forms attached to this plan)
  const numericPlanId = planId ? parseInt(planId, 10) : null;
  const { data: planFormAssociations = [] } = useQuery<any[]>({
    queryKey: [`/api/plans/${numericPlanId}/forms`],
    enabled: !!numericPlanId,
  });

  // All documents (any category) used to show a complete list for this plan
  const {
    documents: allDocuments,
    loading: allDocsLoading,
    error: allDocsError,
    refreshDocuments: allDocsRefresh,
  } = useDocuments({ status: "active" });

  // Helper to view a document in a new tab (inline when supported)
  const handleViewDocument = async (doc: any) => {
    try {
      const res = await fetch(
        `/api/documents/${doc.id}/download?disposition=inline`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to get document URL");
      const { downloadUrl } = await res.json();
      window.open(downloadUrl, "_blank");
    } catch (err) {
      console.error("View document error", err);
    }
  };

  // Eligibility state moved below (to avoid duplicate declarations)

  // Plan-specific benefit categories keyed by BenefitType enum values
  const getBenefitCategories = (planType: string): { key: string; label: string }[] => {
    switch (planType?.toLowerCase()) {
      case "health":
      case "medical":
        return [
          { key: "medical", label: "Medical (Deductible, Copay, OOP)" },
          { key: "specialist", label: "Specialist" },
          { key: "emergency", label: "Emergency Room" },
          { key: "urgent_care", label: "Urgent Care" },
          { key: "hospital", label: "Hospital / Inpatient" },
          { key: "surgery", label: "Surgery / Ambulatory" },
          { key: "prescription", label: "Prescription Drugs" },
          { key: "preventive", label: "Preventive Care" },
          { key: "mental_health", label: "Mental Health" },
          { key: "maternity", label: "Maternity Care" },
        ];
      case "dental":
        return [
          { key: "dental_basic", label: "Basic Services" },
          { key: "dental_major", label: "Major Services" },
          { key: "orthodontia", label: "Orthodontia" },
          { key: "preventive", label: "Preventive Care" },
        ];
      case "vision":
        return [
          { key: "vision_exam", label: "Eye Exam" },
          { key: "vision_frames", label: "Frames Allowance" },
          { key: "vision_lenses", label: "Lens Coverage" },
        ];
      case "life":
        return [
          { key: "life_basic", label: "Basic Life" },
          { key: "life_voluntary", label: "Voluntary Life" },
          { key: "life_spouse", label: "Spouse Coverage" },
          { key: "life_child", label: "Child Coverage" },
          { key: "accidental_death", label: "Accidental Death & Dismemberment" },
        ];
      case "disability":
        return [
          { key: "short_term_disability", label: "Short-Term Disability" },
          { key: "long_term_disability", label: "Long-Term Disability" },
        ];
      case "std":
        return [
          { key: "short_term_disability", label: "Short-Term Disability" },
        ];
      case "ltd":
        return [
          { key: "long_term_disability", label: "Long-Term Disability" },
        ];
      case "accident":
        return [
          { key: "accident_fracture", label: "Fracture Benefit" },
          { key: "accident_hospitalization", label: "Hospitalization" },
          { key: "accident_icu", label: "ICU Admission" },
          { key: "accident_emergency", label: "Emergency Room" },
          { key: "accident_ambulance", label: "Ambulance" },
          { key: "accident_surgery", label: "Surgery" },
          { key: "accident_follow_up", label: "Follow-Up Treatment" },
        ];
      case "critical_illness":
        return [
          { key: "critical_illness_cancer", label: "Cancer" },
          { key: "critical_illness_heart_attack", label: "Heart Attack" },
          { key: "critical_illness_stroke", label: "Stroke" },
          { key: "critical_illness_organ_transplant", label: "Organ Transplant" },
          { key: "critical_illness_renal_failure", label: "Renal Failure" },
          { key: "critical_illness_major_burns", label: "Major Burns" },
        ];
      case "health_care_fsa":
      case "dependent_care_fsa":
        return [
          { key: "medical", label: "FSA Coverage Details" },
        ];
      default:
        return [
          { key: "medical", label: "Coverage Details" },
        ];
    }
  };

  // DEV-310: this used to overlay a `_customizations` localStorage cache
  // onto the live plan (`{...basePlan, ...planCustomizations}`) - no code
  // anywhere writes that key, so it could only ever serve stale data from a
  // leftover browser cache, permanently, since planCustomizations was never
  // re-synced from the backend. Reduced to a passthrough; kept as a
  // function (rather than inlining `plan` at every call site) so the ~40
  // downstream `effectivePlan.*` usages in this file need no changes.
  const getEffectivePlan = (
    basePlan: PlanWithCarrier | undefined
  ): PlanWithCarrier | undefined => basePlan;

  const { data: plan, isLoading: planLoading } = useQuery<PlanWithCarrier>({
    queryKey: [`/api/plans/${planId}`],
    enabled: !!planId,
  });

  const { data: planContributions = [] } = useQuery({
    queryKey: ["/api/plan-contributions"],
  });

  // FSA plans are "configured" via fsaPlanConfig (annual election limits), not contributions.
  const { data: fsaConfig } = useQuery<any>({
    queryKey: [`/api/plans/${planId}/fsa-config`],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/fsa-config`, { credentials: "include" });
      return res.ok ? res.json() : null;
    },
    enabled: !!planId,
  });

  const { data: savedBenefits = {}, isSuccess: benefitsQueryDone } = useQuery({
    queryKey: [`/api/plan-benefits/${planId}`],
    enabled: !!planId,
  });

  // Refetch rates when a child PlanRates editor dispatches "rates:saved".
  useEffect(() => {
    const onRatesSaved = () => {
      queryClient.invalidateQueries({
        queryKey: clientId
          ? ["/api/plan-rates/", planId, clientId]
          : ["/api/plan-rates/", planId],
        refetchType: "active",
      });
    };
    window.addEventListener("rates:saved", onRatesSaved as any);
    return () => window.removeEventListener("rates:saved", onRatesSaved as any);
  }, [planId, clientId, queryClient]);

  // Fetch life/disability rate config for inline editing in Rates & Contributions tab
  useEffect(() => {
    if (!planId || !plan || !isLifeDisabilityType(plan.type)) return;
    setRateConfigLoading(true);
    fetch(`/api/plans/${planId}/life-disability-config`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setRateConfigData({
            ratePerUnit: data.ratePerUnit || "",
            rateUnitBasis: data.rateUnitBasis || "",
            rateBasis: data.rateBasis || "",
            rateType: data.rateType || "",
            tobaccoDistinction: data.tobaccoDistinction || false,
            tobaccoRate: data.tobaccoRate || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setRateConfigLoading(false));
  }, [planId, plan]);

  const saveRateConfig = async () => {
    if (!planId) return;
    setRateConfigSaving(true);
    try {
      const cleanedData = Object.fromEntries(
        Object.entries(rateConfigData).filter(([_, v]) => v !== "")
      );
      const res = await fetch(`/api/plans/${planId}/life-disability-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedData),
      });
      if (!res.ok) throw new Error("Failed to save");
      const saved = await res.json();
      setRateConfigData({
        ratePerUnit: saved.ratePerUnit || "",
        rateUnitBasis: saved.rateUnitBasis || "",
        rateBasis: saved.rateBasis || "",
        rateType: saved.rateType || "",
        tobaccoDistinction: saved.tobaccoDistinction || false,
        tobaccoRate: saved.tobaccoRate || "",
      });

      // Also save age-band rates when in tiered mode
      if (rateConfigData.rateType === "tiered") {
        await saveLifeDisabilityAgeBandRates();
      }

      toast({ title: "Saved", description: "Rate configuration updated." });
    } catch {
      toast({ title: "Error", description: "Failed to save rate configuration.", variant: "destructive" });
    } finally {
      setRateConfigSaving(false);
    }
  };

  // Load age-band rates for life/disability plans (any LD plan may have age rates)
  useEffect(() => {
    if (!planId || !plan || !isLifeDisabilityType(plan.type)) return;
    setLdAgeBandLoading(true);
    fetch(`/api/plan-rates/${planId}${clientId ? `?clientId=${clientId}` : ''}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.rates && data.rates.length > 0) {
          const rates: Record<string, string> = {};
          const tobaccoRates: Record<string, string> = {};
          let detectedBandType = "all_ages";
          for (const r of data.rates) {
            if (r.ageBand != null) {
              rates[String(r.ageBand)] = String(r.monthlyRate ?? "");
              if (r.tobaccoMonthlyRate != null) {
                tobaccoRates[String(r.ageBand)] = String(r.tobaccoMonthlyRate);
              }
              if (r.ageBandType) {
                detectedBandType = r.ageBandType;
              }
            }
          }
          if (Object.keys(rates).length > 0) {
            setLdAgeBandRates(rates);
            setLdAgeBandTobaccoRates(tobaccoRates);
            setLdAgeBandType(detectedBandType);
          }
          // If rates exist, the plan is tiered — sync rateConfigData if not already set
          if (Object.keys(rates).length > 0 && rateConfigData.rateType !== "tiered") {
            setRateConfigData((prev: Record<string, any>) => ({ ...prev, rateType: "tiered" }));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLdAgeBandLoading(false));
  }, [planId, plan]);

  // Generate age band rows — always show full band structure, pre-fill from saved data
  const ldAgeBandRows = useMemo(() => {
    let bands: string[];
    if (ldAgeBandType === "all_ages") {
      bands = ["0-14"];
      for (let i = 15; i <= 63; i++) bands.push(String(i));
      bands.push("64+");
    } else if (ldAgeBandType === "five_year") {
      bands = ["0-17", "18-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "64+"];
    } else {
      bands = ["0-17", "18-29", "30-39", "40-49", "50-59", "60-69", "70+"];
    }
    return bands.map((age) => ({ age, rate: ldAgeBandRates[age] || "" }));
  }, [ldAgeBandType, ldAgeBandRates]);

  const saveLifeDisabilityAgeBandRates = async () => {
    if (!planId) return;
    const filteredRates: Record<string, string> = {};
    const filteredTobaccoRates: Record<string, string> = {};
    ldAgeBandRows.forEach(({ age }) => {
      filteredRates[age] = ldAgeBandRates[age] ?? "";
      if (rateConfigData.tobaccoDistinction && ldAgeBandTobaccoRates[age]) {
        filteredTobaccoRates[age] = ldAgeBandTobaccoRates[age];
      }
    });
    const payload: any = {
      planId: parseInt(planId),
      clientId: clientId ? parseInt(clientId) : null,
      ageBandType: ldAgeBandType,
      ratingMethod: "age_based",
      ageBandRates: filteredRates,
      origin: "manual",
    };
    if (rateConfigData.tobaccoDistinction && Object.keys(filteredTobaccoRates).length > 0) {
      payload.tobaccoAgeBandRates = filteredTobaccoRates;
    }
    const res = await apiRequest("PUT", `/api/plan-age-rates/bulk/${planId}`, payload);
    if (!res.ok) throw new Error("Failed to save age-band rates");
    queryClient.invalidateQueries({ queryKey: ["/api/plan-rates/", planId] });
    queryClient.invalidateQueries({ queryKey: ["/api/plan-rates", planId] });
  };

  // Load benefits from database (takes priority over localStorage)
  useEffect(() => {
    if (!planId || !benefitsQueryDone) return;

    const normalizeBenefitValue = (value: any): BenefitConfig => {
      if (typeof value === "string") return { description: value };
      const v = value as any;
      return {
        description: v.description || "",
        inNetworkDeductible: v.inNetworkDeductible != null ? Number(v.inNetworkDeductible) : undefined,
        outNetworkDeductible: v.outNetworkDeductible != null ? Number(v.outNetworkDeductible) : undefined,
        inNetworkCoinsurance: v.inNetworkCoinsurance != null ? Number(v.inNetworkCoinsurance) : undefined,
        outNetworkCoinsurance: v.outNetworkCoinsurance != null ? Number(v.outNetworkCoinsurance) : undefined,
        inNetworkCopay: v.inNetworkCopay != null ? Number(v.inNetworkCopay) : undefined,
        outNetworkCopay: v.outNetworkCopay != null ? Number(v.outNetworkCopay) : undefined,
        maxOutOfPocket: v.maxOutOfPocket != null ? Number(v.maxOutOfPocket) : undefined,
        annualMaximum: v.annualMaximum != null ? Number(v.annualMaximum) : undefined,
        deductibleApply: v.deductibleApply === true,
      };
    };

    const benefitsData = savedBenefits as Record<string, any>;
    if (Object.keys(benefitsData).length > 0) {
      // API returned benefits — use them
      const normalized: Record<string, BenefitConfig> = {};
      Object.entries(benefitsData).forEach(
        ([key, value]) => {
          if (value && typeof value === "object" || typeof value === "string") {
            normalized[key] = normalizeBenefitValue(value);
          }
        }
      );
      setBenefitDetails(normalized);
    } else {
      // API confirmed no DB data — fall back to localStorage
      const benefitsStorageKey = `client_${
        clientId || "global"
      }_plan_${planId}_benefits`;
      try {
        const localBenefits = localStorage.getItem(benefitsStorageKey);
        if (localBenefits) {
          const benefitsData = JSON.parse(localBenefits);
          const normalized: Record<string, BenefitConfig> = {};
          Object.entries(benefitsData).forEach(([key, value]) => {
            if (key === "savedAt") return;
            if (value && (typeof value === "object" || typeof value === "string")) {
              normalized[key] = normalizeBenefitValue(value);
            }
          });
          setBenefitDetails(normalized);
        }
      } catch {}
    }
  }, [planId, clientId, savedBenefits, benefitsQueryDone]);

  // Define planContribution after queries are available
  const planContribution = Array.isArray(planContributions)
    ? planContributions.find((pc: any) => pc.planId === parseInt(planId || "0"))
    : null;

  // Fetch employees for ineligible employees selection - only for valid numeric client IDs
  const { data: employeesData = [] } = useQuery({
    queryKey: ["/api/clients", clientId, "employees"],
    queryFn: async () => {
      if (!isValidNumericClientId) return [] as any[];
      const res = await fetch(`/api/clients/${clientId}/employees`, {
        credentials: "include",
      });
      if (!res.ok) return [] as any[];
      return res.json();
    },
    enabled: !!isValidNumericClientId,
  });

  const employeesList = Array.isArray(employeesData) ? employeesData : [];

  // Load ineligible employees (client+plan scoped) - only for valid numeric client IDs
  const { data: ineligibleFromServer = [], isLoading: ineligibleFromServerLoading } = useQuery({
    queryKey: ["/api/client-plan-ineligible-employees", clientId, planId],
    queryFn: async () => {
      if (!isValidNumericClientId || !planId) return [];
      const res = await fetch(
        `/api/client-plan-ineligible-employees?clientId=${clientId}&planId=${planId}`
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!isValidNumericClientId && !!planId,
  });

  // Load client-plan eligibility extras (e.g., min hours) - only for valid numeric client IDs
  const { data: clientPlanEligibilityExtras, isLoading: clientPlanEligibilityExtrasLoading } = useQuery({
    queryKey: ["/api/client-plan-eligibility", clientId, planId],
    queryFn: async () => {
      if (!isValidNumericClientId || !planId) return null;
      const res = await fetch(
        `/api/client-plan-eligibility?clientId=${clientId}&planId=${planId}`
      );
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!isValidNumericClientId && !!planId,
  });

  // Filter and sort employees by client and alphabetically
  const clientEmployees = useMemo(() => {
    return sortEmployeesByFullName(employeesList);
  }, [employeesList]);

  // Fetch client-specific plans for contingency dropdown - only for valid numeric client IDs
  const { data: clientPlans = [] } = useQuery({
    queryKey: [`/api/client-plans`, clientId],
    queryFn: () =>
      fetch(`/api/client-plans?clientId=${clientId}`).then((res) => res.json()),
    enabled: !!isValidNumericClientId,
  });

  // Fetch all available plans to cross-reference and filter out deleted ones
  const { data: allPlans = [] } = useQuery({
    queryKey: ["/api/plans"],
    queryFn: () => fetch("/api/plans").then((res) => res.json()),
  });

  // Filter to only include those with existing plans
  const clientPlansArray = Array.isArray(clientPlans) ? clientPlans : [];
  const allPlansArray = Array.isArray(allPlans) ? allPlans : [];
  // DEV-310: this used to read a `client_*_customizations` localStorage
  // key per plan-in-list to compute an `effectiveName` override - that key
  // has no writer anywhere, so it could only ever show a stale cached name.
  // The one render site below already falls back to `clientPlan.plan?.name`
  // when `.effectiveName` is absent, so dropping this .map() step entirely
  // is a no-op for rendering.
  // One entry per plan: a renewed plan has a link per plan year.
  const availableClientPlans = onePlanYearLinkPerPlan(
    clientPlansArray.filter((clientPlan: any) => {
      const planExists = allPlansArray.some(
        (plan: any) => plan.id === clientPlan.planId
      );
      return planExists && clientPlan.plan; // Only include if plan exists and has plan data
    }),
    (clientPlan: any) => Number(clientPlan.planId),
  );

  // Plan year being viewed (?clientPlanId, set by the plan list's View
  // Details) — the Rates tab resolves the rate version for that plan year.
  const viewedLinkId = Number(urlParams.get("clientPlanId"));
  const viewedPlanYearStart: string | undefined = (() => {
    const link = clientPlansArray.find(
      (cp: any) => cp.id === viewedLinkId && cp.planId === planIdNumber,
    );
    return link?.effectiveDate ? String(link.effectiveDate).slice(0, 10) : undefined;
  })();

  // Initialize first benefit on plan load
  useEffect(() => {
    if (plan && !selectedBenefit) {
      const categories = getBenefitCategories(plan.type);
      if (categories.length > 0) {
        setSelectedBenefit(categories[0].key);
      }
    }
  }, [plan, selectedBenefit]);

  useEffect(() => {
    setRawBenefitsDraft(((plan as any)?.rawBenefits as RawBenefitLine[] | null) ?? []);
    setRawBenefitsDirty(false);
  }, [(plan as any)?.id, (plan as any)?.rawBenefits]);

  const saveRawBenefitsMutation = useMutation({
    mutationFn: async (lines: RawBenefitLine[]) => {
      const normalized = normalizeRawBenefits(lines);
      return apiRequest("PATCH", `/api/plans/${planId}`, { rawBenefits: normalized.length ? normalized : null });
    },
    onSuccess: async () => {
      setRawBenefitsDirty(false);
      await queryClient.invalidateQueries({ queryKey: [`/api/plans/${planId}`] });
      toast({ title: "Benefits saved", variant: "success" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save benefits", variant: "destructive" });
    },
  });

  const saveBenefitsMutation = useMutation({
    mutationFn: async (benefits: Record<string, BenefitConfig>) => {
      return apiRequest("POST", `/api/plan-benefits/bulk`, {
        planId: parseInt(planId!),
        benefits,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/plan-benefits/${planId}`],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save benefits to database",
        variant: "destructive",
      });
    },
  });

  // Eligibility state (for Eligibility tab)
  type EmploymentTypesState = {
    "full-time": boolean;
    "part-time": boolean;
    "variable-hour": boolean;
    seasonal: boolean;
    contractor: boolean;
    intern: boolean;
  };

  const specialEnrollmentEventOptions = [
    { key: "marriage", label: "Marriage" },
    { key: "birth_adoption", label: "Birth/Adoption of Child" },
    { key: "loss_coverage", label: "Loss of Other Coverage" },
    { key: "divorce", label: "Divorce/Legal Separation" },
  ] as const;

  const [eligibilityState, setEligibilityState] = useState({
    minHoursPerWeek: "30",
    waitingPeriod: "0",
    waitingPeriodType: "days",
    entryDate: "immediate",
    taxDetermination: "pre-tax",
    domesticPartnerTaxDetermination: "pre-tax",
    employmentTypes: {
      "full-time": true,
      "part-time": false,
      "variable-hour": false,
      seasonal: false,
      contractor: false,
      intern: false,
    } as EmploymentTypesState,
    childAgeLimit: "26",
    studentExtension: true,
    spouseEligible: true,
    childrenEligible: true,
    // DB-only fields added to UI
    spouseMinAge: "",
    spouseMaxAge: "",
    childrenMinAge: "",
    childrenDisabledMaxAge: "",
    childrenTermination: "end_of_month",
    terminationTiming: "immediate",
    enrollmentRequired: false,
    // DEV-148: primary eligibility group's identity + group-level filters,
    // edited inline alongside the rest of the Eligibility Requirements form
    // and persisted on the same Save click.
    groupName: "",
    allowedStates: [] as string[],
    allowedLocationIds: [] as string[],
    classIds: [] as string[],
  });

  // DEV-148: prefill the full eligibility form from the active group every
  // time selection changes. Always overwrites — null group fields render as
  // schema defaults so switching groups is visually obvious (no stale values
  // from the previously selected group bleed through).
  useEffect(() => {
    if (!activeEligibilityGroup) return;
    const g = activeEligibilityGroup;
    const empTypes = Array.isArray(g.employmentTypes) ? g.employmentTypes : [];
    const empTypesMap: EmploymentTypesState = {
      "full-time": empTypes.includes("full-time"),
      "part-time": empTypes.includes("part-time"),
      "variable-hour": empTypes.includes("variable-hour"),
      seasonal: empTypes.includes("seasonal"),
      contractor: empTypes.includes("contractor"),
      intern: empTypes.includes("intern"),
    } as EmploymentTypesState;
    console.log("[PlanDetail] active group changed → prefilling form", {
      groupId: g.id,
      name: g.name,
      hasPerGroupSettings: g.waitingPeriod != null || g.spouseEligible != null,
    });
    setEligibilityState((prev) => ({
      ...prev,
      groupName: g.name ?? "",
      allowedStates: g.allowedStates ?? [],
      allowedLocationIds: (g.allowedLocationIds ?? []).map(String),
      classIds: (g.classIds ?? []).map(String),
      minHoursPerWeek:
        g.minHoursPerWeek != null ? String(g.minHoursPerWeek) : "",
      waitingPeriod: g.waitingPeriod != null ? String(g.waitingPeriod) : "0",
      waitingPeriodType: g.waitingPeriodType ?? "days",
      entryDate: g.entryDate ? g.entryDate.replace(/_/g, "-") : "immediate",
      taxDetermination: g.taxDetermination ?? "pre-tax",
      domesticPartnerTaxDetermination:
        g.domesticPartnerTaxDetermination ?? "pre-tax",
      spouseEligible: g.spouseEligible != null ? g.spouseEligible : true,
      childrenEligible: g.childrenEligible != null ? g.childrenEligible : true,
      enrollmentRequired:
        g.enrollmentRequired != null ? g.enrollmentRequired : false,
      spouseMinAge: g.spouseMinAge != null ? String(g.spouseMinAge) : "",
      spouseMaxAge: g.spouseMaxAge != null ? String(g.spouseMaxAge) : "",
      childrenMinAge: g.childrenMinAge != null ? String(g.childrenMinAge) : "",
      childrenDisabledMaxAge:
        g.childrenDisabledMaxAge != null
          ? String(g.childrenDisabledMaxAge)
          : "",
      childAgeLimit: g.childrenMaxAge != null ? String(g.childrenMaxAge) : "26",
      studentExtension:
        g.childrenStudentMaxAge != null
          ? g.childrenStudentMaxAge !== g.childrenMaxAge
          : true,
      childrenTermination: g.childrenTermination ?? "end_of_month",
      terminationTiming: g.terminationTiming
        ? g.terminationTiming.replace(/_/g, "-")
        : "end-of-month",
      employmentTypes: empTypesMap,
    }));
  }, [activeEligibilityGroup?.id]);

  const [specialEnrollmentEvents, setSpecialEnrollmentEvents] = useState<
    Set<string>
  >(() => new Set(specialEnrollmentEventOptions.map((event) => event.key)));

  const toggleSpecialEnrollmentEvent = (eventKey: string, checked: boolean) => {
    setSpecialEnrollmentEvents((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(eventKey);
      } else {
        next.delete(eventKey);
      }
      return next;
    });
  };

  const [ineligibleEmployeeIds, setIneligibleEmployeeIds] = useState<
    Set<number>
  >(new Set());

  // Track if eligibility state has been initialized to prevent infinite loops
  const eligibilityInitializedRef = useRef<string | null>(null);

  // Initialize eligibility state from plan and localStorage
  useEffect(() => {
    if (!plan) return;
    // DEV-310: wait for both eligibility queries to settle (resolved OR
    // disabled via enabled:false, e.g. no valid clientId) before locking
    // eligibilityInitializedRef below. Previously the ref locked on plan's
    // first resolution regardless of whether these two queries had
    // returned - if plan won that race, this effect would seed state from
    // the localStorage `extras` fallback and then permanently ignore the
    // real server values arriving moments later, even though they're in
    // this effect's own dependency array. isLoading here means
    // isPending && isFetching (React Query v5): false immediately for a
    // disabled query, so the no-clientId case still initializes right away.
    if (clientPlanEligibilityExtrasLoading || ineligibleFromServerLoading) return;

    // Only initialize once per plan + client combination
    const initKey = `${plan.id}-${clientId || "global"}`;
    if (eligibilityInitializedRef.current === initKey) return;
    eligibilityInitializedRef.current = initKey;

    // Load extras (min hours, ineligible employees) from localStorage
    const extrasKey = `client_${clientId || "global"}_plan_${
      plan.id
    }_eligibility_extras`;
    let extras: any = {};
    try {
      const raw = localStorage.getItem(extrasKey);
      extras = raw ? JSON.parse(raw) : {};
    } catch {}

    // Compute employment types map
    const empTypesArray = Array.isArray(plan.employmentTypes)
      ? (plan.employmentTypes as string[])
      : [];
    const empTypesMap: EmploymentTypesState = {
      "full-time": empTypesArray.includes("full-time"),
      "part-time": empTypesArray.includes("part-time"),
      "variable-hour": empTypesArray.includes("variable-hour"),
      seasonal: empTypesArray.includes("seasonal"),
      contractor: empTypesArray.includes("contractor"),
      intern: empTypesArray.includes("intern"),
    } as EmploymentTypesState;

    setEligibilityState((prev) => ({
      minHoursPerWeek:
        clientPlanEligibilityExtras?.minHoursPerWeek?.toString?.() ||
        extras.minHoursPerWeek?.toString?.() ||
        "",
      waitingPeriod:
        plan.waitingPeriod != null
          ? String(plan.waitingPeriod)
          : "0",
      waitingPeriodType:
        (plan.waitingPeriodType as string) || "days",
      entryDate:
        (plan.entryDate as string)?.replace(/_/g, "-") || "immediate",
      taxDetermination:
        (plan.taxDetermination as string) || "pre-tax",
      domesticPartnerTaxDetermination:
        (plan.domesticPartnerTaxDetermination as string) || "pre-tax",
      employmentTypes: empTypesMap,
      childAgeLimit:
        plan.childrenMaxAge != null
          ? String(plan.childrenMaxAge)
          : "26",
      studentExtension:
        plan.childrenStudentMaxAge != null
          ? Number(plan.childrenStudentMaxAge) >
            Number(plan.childrenMaxAge ?? 0)
          : true,
      spouseEligible:
        plan.spouseEligible != null ? Boolean(plan.spouseEligible) : true,
      childrenEligible:
        plan.childrenEligible != null ? Boolean(plan.childrenEligible) : true,
      spouseMinAge:
        plan.spouseMinAge != null
          ? String(plan.spouseMinAge)
          : "",
      spouseMaxAge:
        plan.spouseMaxAge != null
          ? String(plan.spouseMaxAge)
          : "",
      childrenMinAge:
        plan.childrenMinAge != null
          ? String(plan.childrenMinAge)
          : "",
      childrenDisabledMaxAge:
        plan.childrenDisabledMaxAge != null
          ? String(plan.childrenDisabledMaxAge)
          : "",
      childrenTermination:
        (plan.childrenTermination as string) || prev.childrenTermination || "end_of_month",
      terminationTiming:
        (plan.terminationTiming as string)?.replace(/_/g, "-") || prev.terminationTiming,
      enrollmentRequired: Boolean(plan.enrollmentRequired),
      // DEV-148: preserve primary-group fields (filled by separate effect
      // when the eligibility group query resolves).
      groupName: prev.groupName,
      allowedStates: prev.allowedStates,
      allowedLocationIds: prev.allowedLocationIds,
      classIds: prev.classIds,
    }));

    const serverSpecialEvents = clientPlanEligibilityExtras?.specialEnrollmentEvents;
    if (Array.isArray(serverSpecialEvents)) {
      setSpecialEnrollmentEvents(
        new Set(serverSpecialEvents.map((event) => String(event)))
      );
    } else if (Array.isArray(extras.specialEnrollmentEvents)) {
      setSpecialEnrollmentEvents(
        new Set(extras.specialEnrollmentEvents.map((event: any) => String(event)))
      );
    } else {
      setSpecialEnrollmentEvents(
        new Set(specialEnrollmentEventOptions.map((event) => event.key))
      );
    }

    // Prefer server ineligible employees; fall back to local extras
    if (
      Array.isArray(ineligibleFromServer) &&
      ineligibleFromServer.length > 0
    ) {
      setIneligibleEmployeeIds(
        new Set<number>(ineligibleFromServer.map((v: any) => Number(v)))
      );
    } else if (Array.isArray(extras.ineligibleEmployeeIds)) {
      setIneligibleEmployeeIds(
        new Set<number>(extras.ineligibleEmployeeIds.map((v: any) => Number(v)))
      );
    }
  }, [
    plan?.id,
    clientId,
    clientPlanEligibilityExtras?.minHoursPerWeek,
    clientPlanEligibilityExtras?.specialEnrollmentEvents,
    ineligibleFromServer?.length,
    clientPlanEligibilityExtrasLoading,
    ineligibleFromServerLoading,
  ]);

  // Save Eligibility mutation
  const saveEligibilityMutation = useMutation({
    mutationFn: async () => {
      if (!planId) return;

      // Build employment types array
      const employmentTypesArray = Object.entries(
        eligibilityState.employmentTypes
      )
        .filter(([, isOn]) => !!isOn)
        .map(([key]) => key);

      const payload: any = {
        waitingPeriod: parseInt(eligibilityState.waitingPeriod || "0"),
        waitingPeriodType: eligibilityState.waitingPeriodType,
        entryDate: eligibilityState.entryDate.replace(/-/g, "_"),
        taxDetermination: eligibilityState.taxDetermination,
        domesticPartnerTaxDetermination:
          eligibilityState.domesticPartnerTaxDetermination,
        employmentTypes: employmentTypesArray,
        spouseEligible: eligibilityState.spouseEligible,
        childrenEligible: eligibilityState.childrenEligible,
        enrollmentRequired: eligibilityState.enrollmentRequired,
        childrenMaxAge: parseInt(eligibilityState.childAgeLimit || "26"),
        childrenStudentMaxAge: eligibilityState.studentExtension
          ? 26
          : parseInt(eligibilityState.childAgeLimit || "26"),
      };

      // Only include contingencyType if it has a valid value (schema doesn't accept null)
      if (contingencyType) {
        payload.contingencyType = contingencyType;
      }

      if (typeof contingencyPlanId === "number") {
        payload.contingencyPlanId = contingencyPlanId;
      }


      if (eligibilityState.spouseMinAge)
        payload.spouseMinAge = parseInt(eligibilityState.spouseMinAge);
      if (eligibilityState.spouseMaxAge)
        payload.spouseMaxAge = parseInt(eligibilityState.spouseMaxAge);
      if (eligibilityState.childrenMinAge)
        payload.childrenMinAge = parseInt(eligibilityState.childrenMinAge);
      if (eligibilityState.childrenDisabledMaxAge)
        payload.childrenDisabledMaxAge = parseInt(
          eligibilityState.childrenDisabledMaxAge
        );
      if (eligibilityState.childrenTermination)
        payload.childrenTermination =
          eligibilityState.childrenTermination as any;
      if (eligibilityState.terminationTiming)
        payload.terminationTiming =
          eligibilityState.terminationTiming.replace(/-/g, "_") as any;

      // Persist to server for client-plan extras
      if (clientId) {
        const clientIdNum = parseInt(clientId);
        const planIdNum = parseInt(planId!);
        
        if (!isNaN(clientIdNum) && clientIdNum > 0 && !isNaN(planIdNum)) {
          // DEV-220: the per-group eligibility config (job classes, states,
          // waiting period, etc.) is the PRIMARY save for this form — its
          // failure must surface, not be swallowed behind a false "saved"
          // toast. Awaited outside the best-effort block so a rejection
          // propagates to the mutation's onError.
          if (activeEligibilityGroup) {
            await apiRequest(
              "PATCH",
              `/api/eligibility-groups/${activeEligibilityGroup.id}`,
              {
                name: eligibilityState.groupName.trim() || null,
                allowedStates:
                  eligibilityState.allowedStates.length > 0
                    ? eligibilityState.allowedStates
                    : null,
                allowedLocationIds:
                  eligibilityState.allowedLocationIds.length > 0
                    ? eligibilityState.allowedLocationIds.map((s) => Number(s))
                    : null,
                classIds:
                  eligibilityState.classIds.length > 0
                    ? eligibilityState.classIds.map((s) => Number(s))
                    : null,
                employmentTypes:
                  employmentTypesArray.length > 0 ? employmentTypesArray : null,
                minHoursPerWeek: eligibilityState.minHoursPerWeek
                  ? parseInt(eligibilityState.minHoursPerWeek)
                  : null,
                waitingPeriod: parseInt(eligibilityState.waitingPeriod || "0"),
                waitingPeriodType: eligibilityState.waitingPeriodType || null,
                entryDate:
                  eligibilityState.entryDate?.replace(/-/g, "_") || null,
                taxDetermination: eligibilityState.taxDetermination || null,
                domesticPartnerTaxDetermination:
                  eligibilityState.domesticPartnerTaxDetermination || null,
                spouseEligible: eligibilityState.spouseEligible,
                childrenEligible: eligibilityState.childrenEligible,
                enrollmentRequired: eligibilityState.enrollmentRequired,
                childrenMaxAge: parseInt(eligibilityState.childAgeLimit || "26"),
                childrenStudentMaxAge: eligibilityState.studentExtension
                  ? 26
                  : parseInt(eligibilityState.childAgeLimit || "26"),
                spouseMinAge: eligibilityState.spouseMinAge
                  ? parseInt(eligibilityState.spouseMinAge)
                  : null,
                spouseMaxAge: eligibilityState.spouseMaxAge
                  ? parseInt(eligibilityState.spouseMaxAge)
                  : null,
                childrenMinAge: eligibilityState.childrenMinAge
                  ? parseInt(eligibilityState.childrenMinAge)
                  : null,
                childrenDisabledMaxAge: eligibilityState.childrenDisabledMaxAge
                  ? parseInt(eligibilityState.childrenDisabledMaxAge)
                  : null,
                childrenTermination:
                  eligibilityState.childrenTermination || null,
                terminationTiming:
                  eligibilityState.terminationTiming?.replace(/-/g, "_") || null,
              },
            );
          }

          // Auxiliary / back-compat writes — best-effort, must not fail the
          // save. Plan-level PUT below also keeps the primary group in sync
          // for downstream consumers that haven't switched to group reads.
          try {
            const specialEvents = specialEnrollmentEventOptions
              .map((event) => event.key)
              .filter((eventKey) => specialEnrollmentEvents.has(eventKey));
            await Promise.all([
              apiRequest("PUT", "/api/client-plan-eligibility", {
                clientId: clientIdNum,
                planId: planIdNum,
                minHoursPerWeek: parseInt(eligibilityState.minHoursPerWeek || "0"),
                specialEnrollmentEvents: specialEvents,
              }),
              apiRequest("PUT", "/api/client-plan-ineligible-employees", {
                clientId: clientIdNum,
                planId: planIdNum,
                employeeIds: Array.from(ineligibleEmployeeIds).map((id) =>
                  Number(id)
                ),
              }),
            ]);
          } catch (clientSpecificError: any) {
            // Log but don't fail the entire save if these extras fail.
            console.warn('[PlanDetail] Client-specific extras save failed:', clientSpecificError.message);
          }

          // Refetch eligibility groups so the inline form and pickers show the
          // just-saved values. Key types MUST match the query (numeric planId)
          // or this silently won't refetch — see planIdNumber above.
          queryClient.invalidateQueries({
            queryKey: ["/api/eligibility-groups", clientIdNum, planIdNum],
          });
        }
      }

      // Persist unsupported fields to localStorage (fallback)
      const extrasKey = `client_${
        clientId || "global"
      }_plan_${planId}_eligibility_extras`;
      const extras = {
        minHoursPerWeek: eligibilityState.minHoursPerWeek,
        ineligibleEmployeeIds: Array.from(ineligibleEmployeeIds),
        specialEnrollmentEvents: specialEnrollmentEventOptions
          .map((event) => event.key)
          .filter((eventKey) => specialEnrollmentEvents.has(eventKey)),
      };
      try {
        localStorage.setItem(extrasKey, JSON.stringify(extras));
      } catch {}

      return apiRequest("PUT", `/api/plans/${planId}`, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/plans"] });
      await queryClient.invalidateQueries({
        queryKey: [`/api/plans/${planId}`],
      });
      toast({
        title: "Eligibility saved",
        description: "Eligibility settings updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to save eligibility",
        variant: "destructive",
      });
    },
  });

  const handleBack = () => {
    // Employers don't have the broker /clients route — send them to their own benefits page.
    if (isEmployer) {
      setLocation("/benefits");
    } else if (clientId) {
      setLocation(`/clients/${clientId}?tab=benefits`);
    } else if (prospectId) {
      setLocation(`/prospects/${prospectId}?tab=benefits`);
    } else if (window.history.length > 1) {
      window.history.back();
    } else {

      setLocation("/");
    }
  };

  const handleSaveEligibility = () => {
    saveEligibilityMutation.mutate();
  };

  // formsRefresh is provided by useDocuments above

  if (planLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">
              Loading plan details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Get effective plan data (base plan + client customizations)
  const effectivePlan = getEffectivePlan(plan);

  if (!effectivePlan) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Plan not found
          </h1>
          <p className="text-muted-foreground mt-2">
            The requested plan could not be found.
          </p>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{effectivePlan.name}</h1>
            <div className="text-sm text-muted-foreground mt-1">
              Admin Name: {(effectivePlan as any).adminName || "Not specified"}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="capitalize">
                {effectivePlan.type}
              </Badge>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">{effectivePlan.carrier}</span>
              <Badge variant={(effectivePlan as any).isActive ? "default" : "secondary"}>
                {(effectivePlan as any).isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>

        {effectivePlan.needsRateReview ? (
          <button
            type="button"
            onClick={() => markRatesReviewedMutation.mutate()}
            disabled={markRatesReviewedMutation.isPending}
            className="disabled:cursor-not-allowed"
            title="Click to mark reviewed"
          >
            <Badge className="bg-amber-50 text-amber-900 border border-amber-200 cursor-pointer select-none">
              Review plan rates to ensure accuracy
            </Badge>
          </button>
        ) : null}
      </div>

      {/* Warning Banner: not configured for enrollment.
          FSA plans gate on annual election limits (fsaPlanConfig), not contributions. */}
      {isFSAType(effectivePlan?.type)
        ? !isFsaConfigured(fsaConfig) && (
            <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="flex items-start gap-4 py-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800 dark:text-amber-200">
                    FSA Limits Not Configured
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    This plan is not available for employee enrollment until the annual election limits are set.
                    Set the maximum election in the "FSA Config" tab.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                  onClick={() => setActiveTab("fsa-config")}
                >
                  Configure FSA
                </Button>
              </CardContent>
            </Card>
          )
        : !planContribution && (
            <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="flex items-start gap-4 py-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800 dark:text-amber-200">
                    Contributions Not Configured
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    This plan is not available for employee enrollment until employer contributions are configured.
                    Go to the "Rates & Contributions" tab to set up contribution percentages or amounts.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                  onClick={() => setActiveTab("pricing")}
                >
                  Configure Contributions
                </Button>
              </CardContent>
            </Card>
          )}

      {/* Plan Details Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof activeTab)}
        className="space-y-6"
      >
        <TabsList className={`grid w-full ${tabGridCols(
          4 +
            (showsBenefitsTab(effectivePlan?.type) ? 1 : 0) +
            (isLifeDisabilityType(effectivePlan?.type) || isSupplementalType(effectivePlan?.type) ? 1 : 0) +
            (isFSAType(effectivePlan?.type) ? 1 : 0),
        )}`}>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Rates & Contributions</TabsTrigger>
          {showsBenefitsTab(effectivePlan?.type) && (
            <TabsTrigger value="benefits">Benefits</TabsTrigger>
          )}
          <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
          {(isLifeDisabilityType(effectivePlan?.type) || isSupplementalType(effectivePlan?.type)) && (
            <TabsTrigger value="coverage-config">Coverage Config</TabsTrigger>
          )}
          {isFSAType(effectivePlan?.type) && (
            <TabsTrigger value="fsa-config">FSA Config</TabsTrigger>
          )}
          <TabsTrigger value="forms">Forms</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <PlanOverview
            plan={effectivePlan}
            clientId={clientId || undefined}
            onPlanUpdated={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/plans"] });
              queryClient.invalidateQueries({
                queryKey: ["/api/plans", planId],
              });
            }}
          />
        </TabsContent>

        <TabsContent value="pricing" className="space-y-6">
          {isLifeDisabilityType(effectivePlan?.type) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rate Configuration</CardTitle>
                <CardDescription>
                  Rate structure settings for this {effectivePlan?.type?.replace("_", " ")} plan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {rateConfigLoading ? (
                  <p className="text-sm text-muted-foreground">Loading rate config...</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Rate Basis</Label>
                        <Select
                          value={rateConfigData.rateBasis || ""}
                          onValueChange={(value) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, rateBasis: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select rate basis" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="benefit_amount">Per Benefit Amount</SelectItem>
                            <SelectItem value="earnings_amount">Per Earnings Amount</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">What the rate is calculated against</p>
                      </div>
                      <div>
                        <Label>Rate Type</Label>
                        <Select
                          value={rateConfigData.rateType || ""}
                          onValueChange={(value) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, rateType: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select rate type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="composite">Composite Rate</SelectItem>
                            <SelectItem value="tiered">Tiered (Age-Banded) Rates</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Composite = single rate, Tiered = age-banded rates</p>
                      </div>
                    </div>

                    {/* Composite: show Rate Per Unit + Unit Basis */}
                    {rateConfigData.rateType !== "tiered" && (
                      <>
                        <Separator />
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="rc-ratePerUnit">Rate Per Unit</Label>
                            <Input
                              id="rc-ratePerUnit"
                              type="number"
                              step="0.0001"
                              value={rateConfigData.ratePerUnit || ""}
                              onChange={(e) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, ratePerUnit: e.target.value }))}
                              placeholder="0.15"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Premium rate per unit of coverage</p>
                          </div>
                          <div>
                            <Label htmlFor="rc-rateUnitBasis">Rate Unit Basis ($)</Label>
                            <Input
                              id="rc-rateUnitBasis"
                              type="number"
                              step="100"
                              value={rateConfigData.rateUnitBasis || ""}
                              onChange={(e) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, rateUnitBasis: e.target.value }))}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {effectivePlan?.type === "std" || effectivePlan?.type === "ltd"
                                ? "Per $100 of monthly earnings (standard for disability)"
                                : "Per $1,000 of coverage (standard for life)"}
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Tiered: show inline age-band table */}
                    {rateConfigData.rateType === "tiered" && (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-2">
                              <Label className="font-medium">Age Band Structure</Label>
                              <Select value={ldAgeBandType} onValueChange={setLdAgeBandType}>
                                <SelectTrigger className="w-48">
                                  <SelectValue placeholder="Select age band structure" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all_ages">All Ages</SelectItem>
                                  <SelectItem value="five_year">5 Year Age Bands</SelectItem>
                                  <SelectItem value="ten_year">10 Year Age Bands</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {ldAgeBandLoading ? (
                            <p className="text-sm text-muted-foreground">Loading age-band rates...</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Age Range</TableHead>
                                  {rateConfigData.tobaccoDistinction ? (
                                    <>
                                      <TableHead>Non-Tobacco Rate</TableHead>
                                      <TableHead>Tobacco Rate</TableHead>
                                    </>
                                  ) : (
                                    <TableHead>Monthly Rate</TableHead>
                                  )}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {ldAgeBandRows.map(({ age, rate }) => (
                                  <TableRow key={`ld-age:${age}`}>
                                    <TableCell>{age}</TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        value={ldAgeBandRates[age] ?? rate}
                                        onChange={(e) =>
                                          setLdAgeBandRates((prev) => ({
                                            ...prev,
                                            [age]: e.target.value,
                                          }))
                                        }
                                        step="0.01"
                                        className="w-24"
                                      />
                                    </TableCell>
                                    {rateConfigData.tobaccoDistinction && (
                                      <TableCell>
                                        <Input
                                          type="number"
                                          value={ldAgeBandTobaccoRates[age] ?? ""}
                                          onChange={(e) =>
                                            setLdAgeBandTobaccoRates((prev) => ({
                                              ...prev,
                                              [age]: e.target.value,
                                            }))
                                          }
                                          step="0.01"
                                          className="w-24"
                                        />
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}

                        </div>
                      </>
                    )}

                    <Separator />
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="rc-tobaccoDistinction"
                        checked={rateConfigData.tobaccoDistinction || false}
                        onCheckedChange={(checked) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, tobaccoDistinction: checked }))}
                      />
                      <Label htmlFor="rc-tobaccoDistinction" className="cursor-pointer">
                        Separate Tobacco / Non-Tobacco Rates
                      </Label>
                    </div>
                    {rateConfigData.tobaccoDistinction && rateConfigData.rateType !== "tiered" && (
                      <div className="pl-6">
                        <Label>Tobacco Rate (per ${rateConfigData.rateUnitBasis || '1,000'})</Label>
                        <Input
                          type="number"
                          step="0.0001"
                          className="max-w-xs"
                          value={rateConfigData.tobaccoRate}
                          onChange={(e) => setRateConfigData((prev: Record<string, any>) => ({ ...prev, tobaccoRate: e.target.value }))}
                          placeholder="e.g. 0.35"
                        />
                      </div>
                    )}
                    {rateConfigData.tobaccoDistinction && rateConfigData.rateType === "tiered" && (
                      <p className="pl-6 text-sm text-muted-foreground">Tobacco rates are configured per age band in the table above.</p>
                    )}
                    <div className="flex justify-end">
                      <Button onClick={saveRateConfig} disabled={rateConfigSaving} size="sm">
                        {rateConfigSaving ? "Saving..." : "Save Rate Config"}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
          {isFSAType(effectivePlan?.type) ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>FSA plans don't use rates or contributions</AlertTitle>
              <AlertDescription>
                Configure the annual election cap and employer contribution in the FSA Config tab. Employees elect a dollar amount during enrollment.
              </AlertDescription>
            </Alert>
          ) : isLifeDisabilityType(effectivePlan?.type) ? (
            <LifeDisabilityContributions planId={planId!} clientId={clientId || undefined} />
          ) : (
            <Tabs
              value={pricingInnerTab}
              onValueChange={(value) => setPricingInnerTab(value as typeof pricingInnerTab)}
              className="space-y-4"
            >
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="rates">Rates</TabsTrigger>
                <TabsTrigger value="contributions">Contributions</TabsTrigger>
              </TabsList>
              <TabsContent value="rates" className="space-y-6">
                <PlanRates
                  planId={planId!}
                  clientId={clientId || undefined}
                  planType={effectivePlan?.type}
                  planYearStart={viewedPlanYearStart}
                />
              </TabsContent>
              <TabsContent value="contributions" className="space-y-6">
                {clientId && (
                  <ActiveGroupPicker
                    clientId={Number(clientId)}
                    planId={Number(planId)}
                    activeRuleSetId={activeRuleSetId}
                    onChange={(rsId) => {
                      setActiveRuleSetId(rsId);
                    }}
                  />
                )}
                <PlanContributions
                  planId={planId!}
                  clientId={clientId || undefined}
                  activeRuleSetId={activeRuleSetId}
                />
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>

        {showsBenefitsTab(effectivePlan?.type) && (
        <TabsContent value="benefits" className="space-y-6">
          {plan && getBenefitFieldTemplate(plan.type) ? (
          <Card>
            <CardHeader>
              <CardTitle>Benefits</CardTitle>
              <CardDescription>
                {benefitsTabDescription({
                  isEmployer,
                  canEdit: (plan as any)?.permissions?.canEditDetails !== false,
                })}
              </CardDescription>
            </CardHeader>
            {(plan as any)?.permissions?.canEditDetails === false ? (
            <CardContent>
              <WbBenefitsBlock raw={(plan as any)?.rawBenefits ?? []} planType={plan.type} />
            </CardContent>
            ) : (
            <CardContent className="space-y-4">
              <TemplatedBenefitsEditor
                planType={plan.type}
                networkType={(plan as any)?.networkType}
                value={rawBenefitsDraft}
                onChange={(next) => { setRawBenefitsDraft(next); setRawBenefitsDirty(true); }}
              />
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!rawBenefitsDirty || saveRawBenefitsMutation.isPending}
                  onClick={() => { setRawBenefitsDraft(((plan as any)?.rawBenefits as RawBenefitLine[] | null) ?? []); setRawBenefitsDirty(false); }}
                >
                  Discard
                </Button>
                <Button
                  size="sm"
                  disabled={!rawBenefitsDirty || saveRawBenefitsMutation.isPending}
                  onClick={() => saveRawBenefitsMutation.mutate(rawBenefitsDraft)}
                >
                  {saveRawBenefitsMutation.isPending ? "Saving..." : "Save benefits"}
                </Button>
              </div>
            </CardContent>
            )}
          </Card>
          ) : (
          <Card>
            <CardHeader>
              <CardTitle>Benefits Overview</CardTitle>
              <CardDescription>
                Configure plan benefits and coverage details for {plan?.type}{" "}
                plans
              </CardDescription>
              <p className="text-xs text-muted-foreground mt-2">
                Note: Not every benefit requires all fields. The fields you need
                to fill can vary by benefit type. Provide a concise description
                and add advanced details only when they apply.
              </p>
            </CardHeader>
            <CardContent>
              {plan && getBenefitCategories(plan.type).length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Benefit Categories
                  </h4>
                  {getBenefitCategories(plan.type).map(({ key, label }) => {
                    const cfg = benefitDetails[key];
                    const isFilled = !!cfg?.description?.trim();
                    // Build compact inline summary of numeric fields
                    const summaryParts: string[] = [];
                    if (cfg?.inNetworkDeductible != null) summaryParts.push(`Deductible: $${cfg.inNetworkDeductible.toLocaleString()}`);
                    if (cfg?.maxOutOfPocket != null) summaryParts.push(`OOP Max: $${cfg.maxOutOfPocket.toLocaleString()}`);
                    if (cfg?.inNetworkCopay != null) summaryParts.push(`Copay: $${cfg.inNetworkCopay}`);
                    if (cfg?.inNetworkCoinsurance != null) summaryParts.push(`Coinsurance: ${cfg.inNetworkCoinsurance}%`);
                    if (cfg?.annualMaximum != null) summaryParts.push(`Annual Max: $${cfg.annualMaximum.toLocaleString()}`);
                    return (
                      <div
                        key={key}
                        className={`w-full p-3 border rounded-lg transition ${
                          isFilled ? "bg-background" : "bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800"
                        }`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {label}
                            </span>
                            {isFilled ? (
                              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                            ) : (
                              <span className="inline-block w-2 h-2 bg-red-500 rounded-full" />
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-start gap-2">
                              <Textarea
                                placeholder={`Enter ${label.toLowerCase()} details...`}
                                value={cfg?.description || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setBenefitDetails((prev) => {
                                    const next = {
                                      ...prev,
                                      [key]: {
                                        ...(prev[key] || {}),
                                        description: value,
                                      },
                                    } as Record<string, BenefitConfig>;
                                    scheduleAutosave(next);
                                    return next;
                                  });
                                }}
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                title="Configure advanced fields"
                                onClick={() => openAdvanced(key, label)}
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            </div>
                            {summaryParts.length > 0 && (
                              <p className="text-xs text-muted-foreground pl-1">
                                {summaryParts.join(" · ")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    No benefit categories configured for {effectivePlan?.type}{" "}
                    plans.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Advanced Fields Dialog */}
          <Dialog open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Configure{advancedLabel ? ` – ${advancedLabel}` : ""}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Description</Label>
                  <Textarea
                    required
                    placeholder="Enter description..."
                    value={advancedDescription}
                    onChange={(e) => setAdvancedDescription(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>In-network Deductible</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.inNetworkDeductible}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          inNetworkDeductible: e.target.value,
                        }))
                      }
                      placeholder="e.g., 1500"
                    />
                  </div>
                  <div>
                    <Label>Out-of-network Deductible</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.outNetworkDeductible}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          outNetworkDeductible: e.target.value,
                        }))
                      }
                      placeholder="e.g., 3000"
                    />
                  </div>
                  <div>
                    <Label>In-network Coinsurance (%)</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.inNetworkCoinsurance}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          inNetworkCoinsurance: e.target.value,
                        }))
                      }
                      placeholder="e.g., 20"
                    />
                  </div>
                  <div>
                    <Label>Out-of-network Coinsurance (%)</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.outNetworkCoinsurance}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          outNetworkCoinsurance: e.target.value,
                        }))
                      }
                      placeholder="e.g., 40"
                    />
                  </div>
                  <div>
                    <Label>In-network Copay</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.inNetworkCopay}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          inNetworkCopay: e.target.value,
                        }))
                      }
                      placeholder="e.g., 30"
                    />
                  </div>
                  <div>
                    <Label>Out-of-network Copay</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.outNetworkCopay}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          outNetworkCopay: e.target.value,
                        }))
                      }
                      placeholder="e.g., 60"
                    />
                  </div>
                  <div>
                    <Label>Max Out-of-Pocket</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.maxOutOfPocket}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          maxOutOfPocket: e.target.value,
                        }))
                      }
                      placeholder="e.g., 6000"
                    />
                  </div>
                  <div>
                    <Label>Annual Maximum</Label>
                    <Input
                      inputMode="decimal"
                      value={advancedFields.annualMaximum}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          annualMaximum: e.target.value,
                        }))
                      }
                      placeholder="e.g., 2000 (dental)"
                    />
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <input
                      type="checkbox"
                      id="deductibleApply"
                      checked={advancedFields.deductibleApply === "true"}
                      onChange={(e) =>
                        setAdvancedFields((s) => ({
                          ...s,
                          deductibleApply: e.target.checked ? "true" : "false",
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="deductibleApply">Deductible Applies</Label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAdvancedOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!advancedCategory) return;
                    if (!advancedDescription.trim()) {
                      toast({
                        title: "Description required",
                        description: "Please add a description to save.",
                        variant: "destructive",
                      });
                      return;
                    }
                    const parsed: Partial<BenefitConfig> = {
                      inNetworkDeductible: advancedFields.inNetworkDeductible
                        ? Number(advancedFields.inNetworkDeductible)
                        : undefined,
                      outNetworkDeductible: advancedFields.outNetworkDeductible
                        ? Number(advancedFields.outNetworkDeductible)
                        : undefined,
                      inNetworkCoinsurance: advancedFields.inNetworkCoinsurance
                        ? Number(advancedFields.inNetworkCoinsurance)
                        : undefined,
                      outNetworkCoinsurance:
                        advancedFields.outNetworkCoinsurance
                          ? Number(advancedFields.outNetworkCoinsurance)
                          : undefined,
                      inNetworkCopay: advancedFields.inNetworkCopay
                        ? Number(advancedFields.inNetworkCopay)
                        : undefined,
                      outNetworkCopay: advancedFields.outNetworkCopay
                        ? Number(advancedFields.outNetworkCopay)
                        : undefined,
                      maxOutOfPocket: advancedFields.maxOutOfPocket
                        ? Number(advancedFields.maxOutOfPocket)
                        : undefined,
                      annualMaximum: advancedFields.annualMaximum
                        ? Number(advancedFields.annualMaximum)
                        : undefined,
                      deductibleApply: advancedFields.deductibleApply === "true",
                    };

                    setBenefitDetails((prev) => ({
                      ...prev,
                      [advancedCategory]: {
                        ...(prev[advancedCategory] || { description: "" }),
                        description: advancedDescription,
                        ...parsed,
                      },
                    }));

                    // Persist to localStorage and DB (only if description is present)
                    const storageKey = `client_${
                      clientId || "global"
                    }_plan_${planId}_benefits`;
                    const updatedBenefits = {
                      ...benefitDetails,
                      [advancedCategory]: {
                        ...(benefitDetails[advancedCategory] || {
                          description: "",
                        }),
                        description: advancedDescription,
                        ...parsed,
                      },
                      savedAt: new Date().toISOString(),
                    } as Record<string, any>;

                    localStorage.setItem(
                      storageKey,
                      JSON.stringify(updatedBenefits)
                    );

                    const toSave: Record<string, BenefitConfig> = {};
                    Object.entries(updatedBenefits).forEach(([k, v]) => {
                      if (k === "savedAt") return;
                      const obj = v as any;
                      const desc =
                        obj?.description ??
                        (typeof obj === "string" ? obj : "");
                      if (typeof desc === "string" && desc.trim() !== "") {
                        toSave[k] = {
                          description: desc,
                          inNetworkDeductible:
                            obj.inNetworkDeductible != null
                              ? Number(obj.inNetworkDeductible)
                              : undefined,
                          outNetworkDeductible:
                            obj.outNetworkDeductible != null
                              ? Number(obj.outNetworkDeductible)
                              : undefined,
                          inNetworkCoinsurance:
                            obj.inNetworkCoinsurance != null
                              ? Number(obj.inNetworkCoinsurance)
                              : undefined,
                          outNetworkCoinsurance:
                            obj.outNetworkCoinsurance != null
                              ? Number(obj.outNetworkCoinsurance)
                              : undefined,
                          inNetworkCopay:
                            obj.inNetworkCopay != null
                              ? Number(obj.inNetworkCopay)
                              : undefined,
                          outNetworkCopay:
                            obj.outNetworkCopay != null
                              ? Number(obj.outNetworkCopay)
                              : undefined,
                          maxOutOfPocket:
                            obj.maxOutOfPocket != null
                              ? Number(obj.maxOutOfPocket)
                              : undefined,
                          annualMaximum:
                            obj.annualMaximum != null
                              ? Number(obj.annualMaximum)
                              : undefined,
                        };
                      }
                    });
                    saveBenefitsMutation.mutate(toSave);
                    setAdvancedOpen(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        <TabsContent value="eligibility" className="space-y-6">
          {clientId && (
            <EligibilityGroupsPanel
              clientId={Number(clientId)}
              planId={Number(planId)}
              planEmploymentTypes={
                Array.isArray(plan?.employmentTypes)
                  ? (plan.employmentTypes as string[])
                  : null
              }
              activeRuleSetId={activeRuleSetId}
              activeEligibilityGroupId={activeEligibilityGroupId}
              onActiveRuleSetChange={(rsId, gId) => {
                setActiveRuleSetId(rsId);
                if (gId != null) setActiveEligibilityGroupId(gId);
              }}
            />
          )}
          <Card>
            <CardHeader>
              <CardTitle>Eligibility Requirements</CardTitle>
              <CardDescription>
                Define eligibility criteria for plan enrollment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {clientId && eligibilityGroupsAll.length > 1 && (
                  <div className="rounded-md border bg-blue-50/40 dark:bg-blue-950/30 p-3 flex items-center justify-between gap-3">
                    <div className="text-sm">
                      Editing eligibility for group:{" "}
                      <strong>
                        {activeEligibilityGroup?.name ||
                          (activeEligibilityGroup?.priority === 0
                            ? "Primary group"
                            : `Group ${activeEligibilityGroup?.id}`)}
                      </strong>
                    </div>
                    <Select
                      value={
                        activeEligibilityGroupId != null
                          ? String(activeEligibilityGroupId)
                          : ""
                      }
                      onValueChange={(v) => setActiveEligibilityGroupId(Number(v))}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Pick a group…" />
                      </SelectTrigger>
                      <SelectContent>
                        {eligibilityGroupsAll.map((g: any) => (
                          <SelectItem key={g.id} value={String(g.id)}>
                            {g.name ||
                              (g.priority === 0 ? "Primary group" : `Group ${g.id}`)}{" "}
                            · priority {g.priority}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Employee Eligibility Section */}
                <div className="bg-slate-50 dark:bg-slate-900 p-6 rounded-lg border-l-4 border-blue-500">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-blue-700 dark:text-blue-300">
                      Employee Eligibility
                    </h4>
                    <div className="space-y-3">
                      {clientId && (
                        <>
                          <div>
                            <Label htmlFor="group-name">Group name</Label>
                            <Input
                              id="group-name"
                              value={eligibilityState.groupName}
                              onChange={(e) =>
                                setEligibilityState((s) => ({ ...s, groupName: e.target.value }))
                              }
                              placeholder="e.g. All eligible employees"
                            />
                          </div>
                          <div>
                            <Label>Allowed states</Label>
                            <MultiSelect
                              options={US_STATES.map((st) => ({
                                value: st.code,
                                label: `${st.code} — ${st.name}`,
                              }))}
                              selected={eligibilityState.allowedStates}
                              onChange={(v) =>
                                setEligibilityState((s) => ({ ...s, allowedStates: v }))
                              }
                              placeholder="Any state"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Locations</Label>
                              <MultiSelect
                                options={orgOptionsForEligibility.locationOptions}
                                selected={eligibilityState.allowedLocationIds}
                                onChange={(v) =>
                                  setEligibilityState((s) => ({ ...s, allowedLocationIds: v }))
                                }
                                placeholder={
                                  orgOptionsForEligibility.locationOptions.length === 0
                                    ? "No locations defined"
                                    : "Any location"
                                }
                                disabled={orgOptionsForEligibility.locationOptions.length === 0}
                              />
                            </div>
                            <div>
                              <Label>Job classes</Label>
                              <MultiSelect
                                options={orgOptionsForEligibility.classOptions}
                                selected={eligibilityState.classIds}
                                onChange={(v) =>
                                  setEligibilityState((s) => ({ ...s, classIds: v }))
                                }
                                placeholder={
                                  orgOptionsForEligibility.classOptions.length === 0
                                    ? "No classes defined"
                                    : "Any class"
                                }
                                disabled={orgOptionsForEligibility.classOptions.length === 0}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      <div>
                        <Label htmlFor="min-hours">
                          Minimum Hours per Week
                        </Label>
                        <Input
                          id="min-hours"
                          type="number"
                          placeholder="e.g., 30"
                          value={eligibilityState.minHoursPerWeek}
                          onChange={(e) =>
                            setEligibilityState((s) => ({
                              ...s,
                              minHoursPerWeek: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="waiting-period">Waiting Period</Label>
                          <Input
                            id="waiting-period"
                            type="number"
                            placeholder="0"
                            min="0"
                            value={eligibilityState.waitingPeriod}
                            onChange={(e) =>
                              setEligibilityState((s) => ({
                                ...s,
                                waitingPeriod: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor="waiting-period-type">
                            Waiting Period Type
                          </Label>
                          <Select
                            value={eligibilityState.waitingPeriodType}
                            onValueChange={(value) =>
                              setEligibilityState((s) => ({
                                ...s,
                                waitingPeriodType: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="months">Months</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="entry-date">Entry Date</Label>
                          <Select
                            value={eligibilityState.entryDate}
                            onValueChange={(value) =>
                              setEligibilityState((s) => ({
                                ...s,
                                entryDate: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select entry date" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="immediate">
                                Immediate
                              </SelectItem>
                              <SelectItem value="first-of-month">
                                First of the Month
                              </SelectItem>
                              <SelectItem value="first-of-following-month">
                                First of the Following Month
                              </SelectItem>
                              <SelectItem value="15th-of-month">
                                15th of the Month
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Configure when employees become eligible for coverage
                        after hire date
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="tax-determination">
                            Tax Determination
                          </Label>
                          <Select
                            value={eligibilityState.taxDetermination}
                            onValueChange={(value) =>
                              setEligibilityState((s) => ({
                                ...s,
                                taxDetermination: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select tax determination" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pre-tax">Pre-tax</SelectItem>
                              <SelectItem value="post-tax">Post-tax</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="domestic-partner-tax-determination">
                            Domestic Partner Tax Determination
                          </Label>
                          <Select
                            value={
                              eligibilityState.domesticPartnerTaxDetermination
                            }
                            onValueChange={(value) =>
                              setEligibilityState((s) => ({
                                ...s,
                                domesticPartnerTaxDetermination: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select domestic partner tax determination" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pre-tax">Pre-tax</SelectItem>
                              <SelectItem value="post-tax">Post-tax</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label>Enrollment Required</Label>
                          <p className="text-xs text-muted-foreground">
                            Employee must enroll — cannot waive this plan
                          </p>
                        </div>
                        <Checkbox
                          checked={eligibilityState.enrollmentRequired}
                          onCheckedChange={(checked) =>
                            setEligibilityState((s) => ({
                              ...s,
                              enrollmentRequired: Boolean(checked),
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="employment-type">
                          Employment Type (Multi-select)
                        </Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="full-time"
                              checked={
                                eligibilityState.employmentTypes["full-time"]
                              }
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    "full-time": Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="full-time">Full-Time</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="part-time"
                              checked={
                                eligibilityState.employmentTypes["part-time"]
                              }
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    "part-time": Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="part-time">Part-Time</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="variable-hour"
                              checked={
                                eligibilityState.employmentTypes[
                                  "variable-hour"
                                ]
                              }
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    "variable-hour": Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="variable-hour">Variable Hour</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="seasonal"
                              checked={
                                eligibilityState.employmentTypes.seasonal
                              }
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    seasonal: Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="seasonal">Seasonal</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="contractor"
                              checked={
                                eligibilityState.employmentTypes.contractor
                              }
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    contractor: Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="contractor">Contractor</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="intern"
                              checked={eligibilityState.employmentTypes.intern}
                              onCheckedChange={(checked) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  employmentTypes: {
                                    ...s.employmentTypes,
                                    intern: Boolean(checked),
                                  },
                                }))
                              }
                            />
                            <Label htmlFor="intern">Intern</Label>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label htmlFor="ineligible-employees">
                          Ineligible Employees
                        </Label>
                        <div className="text-sm text-muted-foreground mb-2">
                          Select employees who are not eligible for this plan
                        </div>
                        <div className="max-h-64 overflow-y-auto border rounded-md p-3 space-y-2">
                          {clientEmployees.length > 0 ? (
                            clientEmployees.map((employee: any) => (
                              <div
                                key={employee.id}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  id={`ineligible-employee-${employee.id}`}
                                  checked={ineligibleEmployeeIds.has(
                                    employee.id
                                  )}
                                  onCheckedChange={(checked) => {
                                    setIneligibleEmployeeIds((prev) => {
                                      const next = new Set(prev);
                                      if (checked) next.add(employee.id);
                                      else next.delete(employee.id);
                                      return next;
                                    });
                                  }}
                                />
                                <Label
                                  htmlFor={`ineligible-employee-${employee.id}`}
                                  className="text-sm cursor-pointer flex-1"
                                >
                                  {employee.firstName} {employee.lastName} -{" "}
                                  {employee.position || "No Position"} (
                                  {employee.department || "No Department"})
                                </Label>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-muted-foreground text-center py-4">
                              No employees found
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Termination Settings Section */}
                <div className="bg-orange-50 dark:bg-orange-950 p-6 rounded-lg border-l-4 border-orange-500">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-orange-700 dark:text-orange-300">
                      Termination Settings
                    </h4>
                    <div>
                      <Label htmlFor="termination-timing">
                        Coverage Termination Timing
                      </Label>
                      <Select
                        value={eligibilityState.terminationTiming}
                        onValueChange={(value) =>
                          setEligibilityState((s) => ({
                            ...s,
                            terminationTiming: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select termination timing" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">
                            Immediately 
                          </SelectItem>
                          <SelectItem value="middle-of-month">
                            15th of the month
                          </SelectItem>
                          <SelectItem value="end-of-month">
                            End of the month
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground mt-2">
                        When an employee waives this plan or their employment is terminated, their coverage will end based on this setting
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contingencies Section */}
                <div className="bg-emerald-50 dark:bg-emerald-950 p-6 rounded-lg border-l-4 border-emerald-500">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-emerald-700 dark:text-emerald-300">
                      Contingencies
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contingency-type">
                          Contingency Type
                        </Label>
                        <Select
                          value={contingencyType || undefined}
                          onValueChange={(value) =>
                            setContingencyType(value === "none" ? "" : value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select contingency type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="enrolled">Enrolled</SelectItem>
                            <SelectItem value="enrolled-bundled">
                              Enrolled Bundled
                            </SelectItem>
                            <SelectItem value="enrolled-bundled-cobra">
                              Enrolled Bundled (COBRA)
                            </SelectItem>
                            <SelectItem value="not-enrolled">
                              Not-Enrolled
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="contingency-plan">
                          Contingency Plan
                        </Label>
                        <Select
                          value={
                            contingencyPlanId
                              ? contingencyPlanId.toString()
                              : undefined
                          }
                          onValueChange={(value) =>
                            setContingencyPlanId(
                              value === "none" ? undefined : parseInt(value)
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select contingency plan" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {availableClientPlans.map((clientPlan: any) => (
                              <SelectItem
                                key={clientPlan.planId}
                                value={clientPlan.planId.toString()}
                              >
                                {clientPlan.effectiveName ||
                                  clientPlan.plan?.name ||
                                  `Plan ${clientPlan.planId}`}
                              </SelectItem>
                            ))}
                            {availableClientPlans.length === 0 && (
                              <SelectItem value="none" disabled>
                                No plans available for this client
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Configure contingency settings for plan enrollment
                      scenarios
                    </div>
                  </div>
                </div>

                {/* Dependent Eligibility Section */}
                <div className="bg-purple-50 dark:bg-purple-950 p-6 rounded-lg border-l-4 border-purple-500">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-purple-700 dark:text-purple-300">
                      Dependent Eligibility
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="spouse"
                          checked={eligibilityState.spouseEligible}
                          onCheckedChange={(checked) =>
                            setEligibilityState((s) => ({
                              ...s,
                              spouseEligible: Boolean(checked),
                            }))
                          }
                        />
                        <Label htmlFor="spouse">Spouse/Domestic Partner</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="children"
                          checked={eligibilityState.childrenEligible}
                          onCheckedChange={(checked) =>
                            setEligibilityState((s) => ({
                              ...s,
                              childrenEligible: Boolean(checked),
                            }))
                          }
                        />
                        <Label htmlFor="children">Children</Label>
                      </div>
                      <div className="ml-6 space-y-2">
                        <div>
                          <Label htmlFor="child-age-limit">
                            Child Age Limit
                          </Label>
                          <Input
                            id="child-age-limit"
                            type="number"
                            placeholder="e.g., 26"
                            value={eligibilityState.childAgeLimit}
                            onChange={(e) =>
                              setEligibilityState((s) => ({
                                ...s,
                                childAgeLimit: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="student-extension"
                            checked={eligibilityState.studentExtension}
                            onCheckedChange={(checked) =>
                              setEligibilityState((s) => ({
                                ...s,
                                studentExtension: Boolean(checked),
                              }))
                            }
                          />
                          <Label htmlFor="student-extension">
                            Student Extension to Age 26
                          </Label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                          <div>
                            <Label htmlFor="spouse-min-age">
                              Spouse Min Age
                            </Label>
                            <Input
                              id="spouse-min-age"
                              type="number"
                              placeholder="e.g., 18"
                              value={eligibilityState.spouseMinAge}
                              onChange={(e) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  spouseMinAge: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="spouse-max-age">
                              Spouse Max Age
                            </Label>
                            <Input
                              id="spouse-max-age"
                              type="number"
                              placeholder="e.g., 65"
                              value={eligibilityState.spouseMaxAge}
                              onChange={(e) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  spouseMaxAge: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="children-min-age">
                              Children Min Age
                            </Label>
                            <Input
                              id="children-min-age"
                              type="number"
                              placeholder="e.g., 0"
                              value={eligibilityState.childrenMinAge}
                              onChange={(e) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  childrenMinAge: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="children-disabled-max-age">
                              Disabled Child Max Age
                            </Label>
                            <Input
                              id="children-disabled-max-age"
                              type="number"
                              placeholder="leave blank for no limit"
                              value={eligibilityState.childrenDisabledMaxAge}
                              onChange={(e) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  childrenDisabledMaxAge: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor="children-termination">
                              Children Termination
                            </Label>
                            <Select
                              value={eligibilityState.childrenTermination}
                              onValueChange={(value) =>
                                setEligibilityState((s) => ({
                                  ...s,
                                  childrenTermination: value,
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select termination" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="immediate">
                                  Immediate
                                </SelectItem>
                                <SelectItem value="end_of_month">
                                  End of Month
                                </SelectItem>
                                <SelectItem value="end_of_calendar_year">
                                  End of Calendar Year
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Special Enrollment Events Section */}
                <div className="bg-orange-50 dark:bg-orange-950 p-6 rounded-lg border-l-4 border-orange-500">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-orange-700 dark:text-orange-300">
                      Special Enrollment Events
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {specialEnrollmentEventOptions.map((event) => (
                        <div
                          key={event.key}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`special-event-${event.key}`}
                            checked={specialEnrollmentEvents.has(event.key)}
                            onCheckedChange={(checked) =>
                              toggleSpecialEnrollmentEvent(
                                event.key,
                                checked === true
                              )
                            }
                          />
                          <Label htmlFor={`special-event-${event.key}`}>
                            {event.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleSaveEligibility}
                    disabled={saveEligibilityMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveEligibilityMutation.isPending
                      ? "Saving..."
                      : "Save Eligibility"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coverage Config Tab (Life/Disability/Accident/CI) */}
        {(isLifeDisabilityType(effectivePlan?.type) || isSupplementalType(effectivePlan?.type)) && (
          <TabsContent value="coverage-config" className="space-y-6">
            {isLifeDisabilityType(effectivePlan?.type) ? (
              <LifeDisabilityConfigForm
                planId={parseInt(planId!)}
                planType={
                  effectivePlan?.type === "std" || effectivePlan?.type === "ltd"
                    ? effectivePlan.type as "std" | "ltd"
                    : effectivePlan?.type === "voluntary_life"
                    ? "voluntary_life"
                    : effectivePlan?.type === "add"
                    ? "add"
                    : effectivePlan?.type === "life"
                    ? "life"
                    : "disability"
                }
                onSave={() => {
                  toast({
                    title: "Configuration saved",
                    description: "Coverage configuration has been saved successfully.",
                  });
                }}
              />
            ) : (
              <SupplementalConfigForm
                planId={parseInt(planId!)}
                planType={effectivePlan?.type === "accident" ? "accident" : "critical_illness"}
                onSave={() => {
                  toast({
                    title: "Configuration saved",
                    description: "Supplemental plan configuration has been saved.",
                  });
                }}
              />
            )}
          </TabsContent>
        )}

        {/* FSA Config Tab */}
        {isFSAType(effectivePlan?.type) && (
          <TabsContent value="fsa-config" className="space-y-6">
            <FSAConfigForm
              planId={parseInt(planId!)}
              fsaType={
                effectivePlan?.type === "fsa_dependent_care" || effectivePlan?.type === "dependent_care_fsa"
                  ? "dependent_care"
                  : "health_care"
              }
              onSave={() => {
                toast({
                  title: "FSA configuration saved",
                  description: "FSA plan settings have been updated.",
                });
              }}
            />
          </TabsContent>
        )}

        <TabsContent value="forms" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Forms & Documents</CardTitle>
              <CardDescription>
                Manage plan-related forms and documentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Plan documents (SBCs, summaries, links) — employees can
                    download these from their benefits view. */}
                <PlanDocumentsManager planId={effectivePlan.id} />

                <Separator />

                {/* Form Library — pick forms from the library to attach to this plan */}
                <PlanFormLibraryPanel planId={effectivePlan.id} carrierId={effectivePlan.carrierId} />

                <Separator />

                {/* Plan-specific documents (category: form) */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Plan Forms</h4>
                    <DocumentUpload
                      ownerType="agency"
                      defaultCategory="form"
                      categoryOptions={[
                        { value: "sbc", label: "SBC" },
                        { value: "eoc", label: "EOC" },
                        { value: "application", label: "Application" },
                        { value: "waiver", label: "Waiver" },
                        { value: "form", label: "Form" },
                        { value: "other", label: "Other" },
                      ]}
                      tags={
                        [
                          `plan:${effectivePlan.id}`,
                          effectivePlan.type
                            ? `planType:${String(effectivePlan.type)}`
                            : "",
                          clientId ? `client:${clientId}` : "",
                        ].filter(Boolean) as string[]
                      }
                      trigger={
                        <Button variant="outline" size="sm">
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Document
                        </Button>
                      }
                      onUploadSuccess={() => {
                        // refresh lists after upload
                        formsRefresh();
                        allDocsRefresh();
                      }}
                    />
                  </div>
                  <div>
                    {formsError && (
                      <div className="text-sm text-destructive">
                        {formsError}
                      </div>
                    )}
                    {formsLoading ? (
                      <div className="text-sm text-muted-foreground">
                        Loading forms…
                      </div>
                    ) : (
                      <DocumentList
                        documents={forms.filter(
                          (d: any) =>
                            Array.isArray(d.tags) &&
                            d.tags.includes(`plan:${effectivePlan.id}`)
                        )}
                        onDocumentUpdate={formsRefresh}
                      />
                    )}
                  </div>
                </div>

                {/* All plan documents (any category) */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Plan Documents</h4>
                    <DocumentUpload
                      ownerType="agency"
                      defaultCategory="other"
                      categoryOptions={[
                        { value: "sbc", label: "SBC" },
                        { value: "eoc", label: "EOC" },
                        { value: "application", label: "Application" },
                        { value: "waiver", label: "Waiver" },
                        { value: "policy", label: "Policy" },
                        { value: "compliance", label: "Compliance" },
                        { value: "other", label: "Other" },
                      ]}
                      tags={
                        [
                          `plan:${effectivePlan.id}`,
                          effectivePlan.type
                            ? `planType:${String(effectivePlan.type)}`
                            : "",
                          clientId ? `client:${clientId}` : "",
                        ].filter(Boolean) as string[]
                      }
                      trigger={
                        <Button variant="outline" size="sm">
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Document
                        </Button>
                      }
                      onUploadSuccess={() => {
                        allDocsRefresh();
                        formsRefresh();
                      }}
                    />
                  </div>
                  <div>
                    {allDocsError && (
                      <div className="text-sm text-destructive">
                        {allDocsError}
                      </div>
                    )}
                    {allDocsLoading ? (
                      <div className="text-sm text-muted-foreground">
                        Loading documents…
                      </div>
                    ) : (
                      <DocumentList
                        documents={(allDocuments as any[]).filter(
                          (d: any) =>
                            Array.isArray(d.tags) &&
                            d.tags.includes(`plan:${effectivePlan.id}`)
                        )}
                        onDocumentUpdate={() => {
                          allDocsRefresh();
                          formsRefresh();
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Enrollment Forms</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        label: "Enrollment Application",
                        tag: "type:enrollment-application",
                        formTypes: ["enrollment", "application", "group_application"],
                      },
                      {
                        label: "Change Form",
                        tag: "type:change-form",
                        formTypes: ["change"],
                      },
                      {
                        label: "Waiver Form",
                        tag: "type:waiver-form",
                        formTypes: ["waiver"],
                      },
                      {
                        label: "Termination Form",
                        tag: "type:termination-form",
                        formTypes: ["termination"],
                      },
                    ].map((item) => (
                      <div
                        key={item.tag}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">
                            {item.label}
                          </span>
                          <DocumentUpload
                            ownerType="agency"
                            defaultCategory="form"
                            fixedCategory
                            tags={[`plan:${effectivePlan.id}`, item.tag]}
                            trigger={
                              <Button variant="outline" size="sm">
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                              </Button>
                            }
                            onUploadSuccess={() => formsRefresh?.()}
                          />
                        </div>
                        <div className="min-w-0">
                          {(() => {
                            // Forms attached via the Form Library matching this slot's formTypes
                            const libraryMatches = (planFormAssociations as any[]).filter(
                              (assoc: any) =>
                                assoc?.form?.formType &&
                                item.formTypes.includes(String(assoc.form.formType))
                            );

                            // Legacy uploaded documents matching this slot's tag
                            const uploadedMatches = (forms as any[])
                              .filter(
                                (d: any) =>
                                  Array.isArray(d.tags) &&
                                  d.tags.includes(`plan:${effectivePlan.id}`) &&
                                  d.tags.includes(item.tag)
                              )
                              .sort(
                                (a: any, b: any) =>
                                  new Date(b.uploadedAt).getTime() -
                                  new Date(a.uploadedAt).getTime()
                              );

                            if (libraryMatches.length === 0 && uploadedMatches.length === 0) {
                              return (
                                <p className="text-sm text-muted-foreground truncate">
                                  No file uploaded yet.
                                </p>
                              );
                            }

                            return (
                              <ul className="space-y-1.5">
                                {libraryMatches.map((assoc: any) => {
                                  const f = assoc.form;
                                  const isCarrierForm =
                                    f.source === "carrier_library" || !!f.carrierFormId || !!assoc.carrierFormId;
                                  const previewUrl = isCarrierForm
                                    ? `/api/carrier-forms/${f.id ?? assoc.carrierFormId}/content`
                                    : f.documentId
                                      ? `/api/documents/${f.documentId}/content?disposition=inline`
                                      : null;
                                  return (
                                    <li key={`lib-${assoc.id}`} className="flex items-center justify-between gap-3 text-sm">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                        <span className="truncate">{f.name}</span>
                                        <Badge variant="secondary" className="text-[10px] shrink-0">
                                          {isCarrierForm ? "Carrier" : "Broker"}
                                        </Badge>
                                      </div>
                                      {previewUrl && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 shrink-0"
                                          onClick={() => window.open(previewUrl, "_blank")}
                                        >
                                          View
                                        </Button>
                                      )}
                                    </li>
                                  );
                                })}
                                {uploadedMatches.map((doc: any) => (
                                  <li key={`up-${doc.id}`} className="flex items-center justify-between gap-3 text-sm">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                      <span className="truncate">{doc.originalFileName}</span>
                                      <Badge variant="outline" className="text-[10px] shrink-0">
                                        Uploaded
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2"
                                        onClick={() => handleViewDocument(doc)}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2"
                                        onClick={async () => {
                                          try {
                                            const res = await fetch(
                                              `/api/documents/${doc.id}/download`,
                                              { credentials: "include" }
                                            );
                                            if (!res.ok)
                                              throw new Error("Failed to get download link");
                                            const { downloadUrl } = await res.json();
                                            window.open(downloadUrl, "_blank");
                                          } catch (e) {
                                            console.error(e);
                                          }
                                        }}
                                      >
                                        Download
                                      </Button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Document Settings</h4>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Document
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="auto-generate">
                        Auto-generate enrollment packets
                      </Label>
                      <Switch id="auto-generate" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="electronic-signature">
                        Enable electronic signatures
                      </Label>
                      <Switch id="electronic-signature" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="document-retention">
                        Document retention (years)
                      </Label>
                      <Input
                        id="document-retention"
                        type="number"
                        placeholder="7"
                        defaultValue="7"
                        className="w-20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
